import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { attributeValueActions } from '../utils/Constants';
import Select from "react-select";
import { debounce } from 'lodash';
import { getUniqueAttributeValues } from '../services/RecategorizeServices';
import { customStyles } from '../utils/Styles';

type AttributeValuesMappingProps = {
    payload: TRecategorizePayload;
    setPayload: React.Dispatch<React.SetStateAction<TRecategorizePayload>>;
    recategorizationResponse: RecategorizationResponse | undefined;
    attributeValueData: AttributeValueMapping | null;
};

const AttributeValuesMapping = ({ payload, setPayload, recategorizationResponse, attributeValueData }: AttributeValuesMappingProps) => {
    const [hasMoreSearch, setHasMoreSearch] = useState(true);
    const [hasMoreNormal, setHasMoreNormal] = useState(true);
    const isMounted = useRef(true);
    const [allTargetValues, setAllTargetValues] = useState<AttributeValue[]>([]);
    const [filteredValues, setFilteredValues] = useState<AttributeValue[]>([]);
    const [normalPage, setNormalPage] = useState(1);
    const [searchPage, setSearchPage] = useState(1);
    const loadedPagesRef = useRef<Set<number>>(new Set([1]));
    const [isSearching, setIsSearching] = useState(false);
    const [searchValue, setSearchValue] = useState<string>("");
    const sourceAttribute = useMemo(() =>
        payload?.unique_attr_source_list?.find(e => e.attribute_id === attributeValueData?.selectedSourceAttribute),
        [payload?.unique_attr_source_list, attributeValueData?.selectedSourceAttribute]
    );

    const targetAttribute = useMemo(() =>
        recategorizationResponse?.unique_attr_target_list?.find(e => e.attribute_id === attributeValueData?.selectedTargetAttribute),
        [recategorizationResponse?.unique_attr_target_list, attributeValueData?.selectedTargetAttribute]
    );

    // From payload - contains mapping info (action, target_attribute_value)
    const sourceAttributeValues = useMemo(() =>
        sourceAttribute?.unique_attribute_values ?? [],
        [sourceAttribute]
    );

    // From recategorizationResponse - original attribute values
    const originalSourceAttributeValues = useMemo(() =>
        recategorizationResponse?.unique_attr_source_list?.find(
            e => e.attribute_id === attributeValueData?.selectedSourceAttribute
        )?.attribute_value ?? [],
        [recategorizationResponse?.unique_attr_source_list, attributeValueData?.selectedSourceAttribute]
    );

    const shouldFetchInitialData = useCallback(() => {
        if (!attributeValueData?.selectedTargetAttribute) return false;
        const targetAttr = recategorizationResponse?.unique_attr_target_list?.find(
            attr => attr.attribute_id === attributeValueData.selectedTargetAttribute
        );
        return !targetAttr || !targetAttr.attribute_value || targetAttr.attribute_value.length === 0;
    }, [attributeValueData?.selectedTargetAttribute, recategorizationResponse?.unique_attr_target_list]);

    const debouncedLoadMoreOptions = useCallback(
        debounce(() => {
            const nextPage = isSearching ? searchPage + 1 : normalPage + 1;

            if (isSearching && hasMoreSearch) {
                fetchTargetValues(nextPage, searchValue);
            } else if (!isSearching && hasMoreNormal && !loadedPagesRef.current.has(nextPage)) {
                fetchTargetValues(nextPage, "");
            }
        }, 300),
        [hasMoreNormal, hasMoreSearch, normalPage, searchPage, isSearching, searchValue]
    );

    const fetchTargetValues = useCallback(async (pageNum: number, search: string) => {
        if (!attributeValueData?.selectedTargetAttribute || !isMounted.current) return

        try {
            const attributeValues = originalSourceAttributeValues.map(attrValue => attrValue.value)
            const formattedAttributeValueIds = JSON.stringify(attributeValues)

            const response = await getUniqueAttributeValues(
                attributeValueData.selectedTargetAttribute,
                pageNum,
                formattedAttributeValueIds,
                search
            )

            if (!response.data?.[0]?.attribute_values) return

            const attributeData = response.data[0]
            const newAttributeValues = attributeData.attribute_values

            if (search) {
                setFilteredValues(prev =>
                    pageNum === 1 ? newAttributeValues : [...prev, ...newAttributeValues]
                )
                setSearchPage(pageNum)
                setHasMoreSearch(pageNum < (attributeData?.totalPages || 1))
            } else {
                setAllTargetValues(prev => {
                    const uniqueValues = [...prev]
                    newAttributeValues.forEach((newVal: AttributeValue) => {
                        if (!uniqueValues.some(existing => existing.id === newVal.id)) {
                            uniqueValues.push(newVal)
                        }
                    })
                    return uniqueValues
                })
                loadedPagesRef.current.add(pageNum)
                setNormalPage(prevPage => Math.max(prevPage, pageNum))
                setHasMoreNormal(pageNum < (attributeData?.totalPages || 1))
            }
        } catch (error) {
            console.error("Error fetching attribute values:", error)
        }
    }, [attributeValueData?.selectedTargetAttribute, originalSourceAttributeValues])

    const resetSearchState = useCallback(() => {
        setFilteredValues([])
        setSearchPage(1)
        setHasMoreSearch(true)
        setIsSearching(false)
        setSearchValue("")
    }, [])

    const handleInputChange = useCallback(
        (input: string, { action }: { action: string }) => {
            if (action === "input-blur" || action === "menu-close") return

            if (input === "") {
                resetSearchState()
                // Don't reset normal page - continue from where it left off
                if (allTargetValues.length === 0) {
                    fetchTargetValues(normalPage, "")
                }
                return
            }

            setSearchValue(input)
            setIsSearching(true)
            setSearchPage(1)
            setHasMoreSearch(true)
            fetchTargetValues(1, input)
        },
        [fetchTargetValues, normalPage, resetSearchState]
    )

    const handleMenuScrollToBottom = useCallback(() => {
        if (isSearching && hasMoreSearch) {
            const nextSearchPage = searchPage + 1
            fetchTargetValues(nextSearchPage, searchValue)
        } else if (!isSearching && hasMoreNormal) {
            const nextNormalPage = normalPage + 1
            if (!loadedPagesRef.current.has(nextNormalPage)) {
                fetchTargetValues(nextNormalPage, "")
            }
        }
    }, [
        isSearching,
        hasMoreSearch,
        hasMoreNormal,
        searchPage,
        normalPage,
        searchValue,
        fetchTargetValues
    ])

    const handleMenuClose = useCallback(() => {
        if (!searchValue) return

        resetSearchState()
        if (allTargetValues.length === 0) {
            fetchTargetValues(normalPage, "")
        }
    }, [searchValue, normalPage, resetSearchState, allTargetValues.length])

    // Initial data fetch on component mount
    useEffect(() => {
        if (attributeValueData?.selectedTargetAttribute && allTargetValues.length === 0) {
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
            fetchTargetValues(1, "")
        }
    }, [attributeValueData?.selectedTargetAttribute, allTargetValues.length, fetchTargetValues])

    // Cleanup
    useEffect(() => {
        return () => {
            isMounted.current = false
            resetSearchState()
            setNormalPage(1)
            loadedPagesRef.current = new Set([1])
        }
    }, [resetSearchState])

    const debouncedSearch = useMemo(
        () =>
            debounce((input: string) => {
                setIsSearching(true);
                setSearchPage(1);
                setHasMoreSearch(true);
                setFilteredValues([]);
            }, 300),
        []
    );

    const getAvailableTargetValues = useCallback((currentValue: string) => {
        const alreadyMappedValues = new Set(
            sourceAttributeValues
                .filter(sourceVal =>
                    sourceVal.action === 'map' &&
                    sourceVal.target_attribute_value &&
                    sourceVal.attribute_value !== currentValue
                )
                .map(sourceVal => sourceVal.target_attribute_value)
        );

        const valuesToUse = isSearching ? filteredValues : allTargetValues;

        return valuesToUse
            .filter(val => !alreadyMappedValues.has(val.value))
            .map(val => ({
                label: val.value,
                value: val.value
            }));
    }, [sourceAttributeValues, allTargetValues, filteredValues, isSearching]);

    const handleAttributeValueActionChange = (attributeValue: string, action: AttributeValueActionType) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeValueData?.selectedSourceAttribute) return attr;

                const existingUniqueValues = attr.unique_attribute_values || [];
                const valueExists = existingUniqueValues.some(val => val.attribute_value === attributeValue);
                const updatedUniqueValues = valueExists
                    ? existingUniqueValues.map(val =>
                        val.attribute_value === attributeValue
                            ? { ...val, action, target_attribute_value: undefined }
                            : val
                    )
                    : [...existingUniqueValues, { attribute_value: attributeValue, action, target_attribute_value: undefined }];

                return {
                    ...attr,
                    unique_attribute_values: updatedUniqueValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    const handleAttributeValueTargetChange = (attributeValue: string, targetValue: string) => {
        setPayload(prev => {
            const updatedUniqueAttrList = prev.unique_attr_source_list.map(attr => {
                if (attr.attribute_id !== attributeValueData?.selectedSourceAttribute) return attr;

                const existingUniqueValues = attr.unique_attribute_values || [];
                const updatedUniqueValues = existingUniqueValues.map(val =>
                    val.attribute_value === attributeValue
                        ? { ...val, target_attribute_value: targetValue }
                        : val
                );

                return {
                    ...attr,
                    unique_attribute_values: updatedUniqueValues
                };
            });

            return {
                ...prev,
                unique_attr_source_list: updatedUniqueAttrList
            };
        });
    };

    useEffect(() => {
        return () => {
            isMounted.current = false;
            debouncedSearch.cancel();
            debouncedLoadMoreOptions.cancel();
            setSearchValue("");
            setNormalPage(1);
            setSearchPage(1);
            setHasMoreNormal(true);
            setHasMoreSearch(true);
        };
    }, []);

    useEffect(() => {
        const loadInitialData = async () => {
            if (shouldFetchInitialData()) {
                setAllTargetValues([]);
                setFilteredValues([]);
                setNormalPage(1);
                setSearchPage(1);
                loadedPagesRef.current = new Set();
                await fetchTargetValues(1, "");
            }
        };

        loadInitialData();
    }, [attributeValueData?.selectedTargetAttribute]);

    const selectProps = useMemo(() => ({
        id: "attributeValueId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: '#0747a6'
            })
        },
        isSearchable: true,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: "Select Attribute Value"
    }), []);

    const actionTypeSelectProps = useMemo(() => ({
        id: "actionTypeId",
        className: "tw-outline-none",
        classNamePrefix: "select",
        styles: {
            ...customStyles,
            loadingIndicator: (base: any) => ({
                ...base,
                color: '#0747a6'
            })
        },
        isSearchable: false,
        maxMenuHeight: 220,
        menuPlacement: "bottom" as const,
        menuPosition: "absolute" as const,
        isClearable: false,
        placeholder: "Select"
    }), []);

    return (
        <div className="tw-w-full tw-max-w-4xl tw-mx-auto tw-bg-white tw-rounded-lg tw-my-4 tw-pb-6">
            <div className="tw-flex tw-flex-col">
                <table className="tw-w-full">
                    <thead className="tw-bg-[#F4F5F7] tw-rounded-t-lg">
                        <tr className="tw-border-[1px] tw-border-[#DADBDD] tw-rounded-t-lg">
                            <th className="tw-w-[5%] tw-px-4 tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">#</th>
                            <th className="tw-w-[40%]  tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">{sourceAttribute?.attribute_name}</th>
                            <th className="tw-w-[20%]  tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">Action</th>
                            <th className="tw-w-[35%]  tw-py-3 tw-text-left tw-font-semibold tw-text-sm tw-text-[#6D788C]">{targetAttribute?.attribute_name}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sourceAttributeValues?.map((row, index) => (
                            <tr key={row.attribute_value} className={`tw-border tw-border-[#DADBDD] ${index % 2 === 0 ? 'tw-bg-white' : 'tw-bg-[#F4F5F7]'}`}>
                                <td className="tw-w-16 tw-pl-4 tw-py-4 tw-text-[#172B4D] tw-text-sm">
                                    {index + 1}
                                </td>
                                <td className="tw-w-1/3 tw-py-4 tw-text-[#172B4D] tw-text-sm">{row.attribute_value}</td>
                                <td className="tw-py-4 tw-pr-8  tw-text-[#172B4D] tw-text-sm">
                                    <Select<OptionType>
                                        {...actionTypeSelectProps}
                                        options={attributeValueActions}
                                        isSearchable={false}
                                        onChange={(value) => handleAttributeValueActionChange(row.attribute_value, value?.value as AttributeValueActionType)}
                                        value={row?.action ? { "label": attributeValueActions.find(x => x.value === row?.action)?.label as AttributeActionLabelType, value: row.action } : undefined}
                                        components={{
                                            IndicatorSeparator: () => null,
                                            NoOptionsMessage: () => (
                                                <div className="tw-p-2 tw-text-center tw-text-gray-500">
                                                    No attribute values available
                                                </div>
                                            )
                                        }}
                                    />
                                </td>
                                <td className="tw-py-4 tw-text-[#172B4D] tw-text-sm tw-pr-8">
                                    {row?.action === 'create' ? row?.attribute_value : row.action === 'map' ? (
                                        <div
                                            title={row.target_attribute_value ?? ''}
                                            className="tw-delay-0 tw-duration-0"
                                        >
                                            <Select<OptionType>
                                                {...selectProps}
                                                options={getAvailableTargetValues(row.attribute_value)}
                                                onMenuScrollToBottom={handleMenuScrollToBottom}
                                                onInputChange={handleInputChange}
                                                onChange={(selectedOption) => {
                                                    if (selectedOption) {
                                                        handleAttributeValueTargetChange(
                                                            row.attribute_value,
                                                            selectedOption.value
                                                        );
                                                    }
                                                }}
                                                onMenuClose={handleMenuClose}
                                                value={row.target_attribute_value
                                                    ? {
                                                        value: row.target_attribute_value,
                                                        label: row.target_attribute_value
                                                    }
                                                    : null
                                                }
                                                components={{
                                                    IndicatorSeparator: () => null,
                                                    NoOptionsMessage: () => (
                                                        <div className="tw-p-2 tw-text-center tw-text-gray-500">
                                                            No attribute values available
                                                        </div>
                                                    )
                                                }}
                                            />
                                        </div>
                                    ) : (
                                        "NA"
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div >
    );
}

export default AttributeValuesMapping